// @flow
import type { Location } from 'types/entities';

type Bounds = [Location, Location];

export function roundCoordinate(value: number): number {
  return parseFloat(value.toFixed(6));
}

export function extendBounds(markers: Array<Location>) {
  const extent: Bounds = [[-Infinity, -Infinity], [Infinity, Infinity]];
  markers.forEach((marker: Location) => {
    if (marker[0] > extent[0][0]) {
      extent[0][0] = marker[0];
    }

    if (marker[0] < extent[1][0]) {
      extent[1][0] = marker[0];
    }

    if (marker[1] > extent[0][1]) {
      extent[0][1] = marker[1];
    }

    if (marker[1] < extent[1][1]) {
      extent[1][1] = marker[1];
    }
  });

  // https://github.com/uber/react-map-gl/issues/83
  return [[extent[0][1], extent[0][0]], [extent[1][1], extent[1][0]]];
}

export function getBoundsZoomLevel(width: number, height: number, bounds: Bounds): number {
  // We have to defined our own function to calculate zoom level given some bounds
  // because react-map-gl's MapGL.fitBounds gives `NaN` for zoom.
  // http://stackoverflow.com/a/13274361/1751946
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;
  // Mapbox zoom level is different from Google's. This value is arbitrarily determined.
  const OFFSET = 2;

  function latRad(lat) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx, worldPx, fraction) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const ne = bounds[0];
  const sw = bounds[1];

  const latFraction = (latRad(ne[1]) - latRad(sw[1])) / Math.PI;

  const lngDiff = ne[0] - sw[0];
  const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

  const latZoom = zoom(height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX) - OFFSET;
}



/** WEBPACK FOOTER **
 ** ./src/js/utils/map-utils.js
 **/